import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2 } from "lucide-react";
import { CONTACT } from "@/constants/testIds";
import { trackFormConversion, trackCallConversion } from "@/lib/tracking";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  "Households",
  "Parks & Schools",
  "Wood-Destroying Insects (Termites)",
  "Wildlife Removal",
  "Mosquito Control",
  "Bed Bugs",
  "Other / Not Sure",
];

const Contact = () => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    message: "",
  });

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      trackFormConversion(res.data?.id);
      setSuccess(true);
      toast.success("Request received. We'll call you shortly.");
      setForm({ name: "", email: "", phone: "", service_type: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us directly at 484-764-3392.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="border-b border-zinc-900 bg-[#0B2B40] text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* LEFT: Info */}
        <div className="lg:col-span-5 flex flex-col">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#F59E0B] font-bold mb-4">
            [ 05 ] — Let's talk
          </p>
          <h2 className="font-display uppercase font-semibold leading-[0.85] tracking-tighter text-5xl md:text-7xl mb-8">
            Got a guy <br />
            <span className="text-[#F59E0B]">for that.</span>
          </h2>
          <p className="text-zinc-300 leading-relaxed text-base md:text-lg max-w-md mb-10">
            Call, text or fill the form. Real humans, real fast. We'll set up your free
            inspection within 48 hours.
          </p>

          <div className="space-y-5">
            <a
              href="tel:4847643392"
              data-testid={CONTACT.phoneLink}
              onClick={() => trackCallConversion("contact_owner_phone")}
              className="flex items-start gap-4 group"
            >
              <div className="w-12 h-12 bg-[#D9480F] flex items-center justify-center border-2 border-white flex-shrink-0">
                <Phone className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-1">
                  Owner · Direct Line
                </div>
                <div className="font-display uppercase text-2xl md:text-3xl tracking-tight group-hover:text-[#F59E0B] transition-colors">
                  484-764-3392
                </div>
              </div>
            </a>

            <a
              href="tel:4124514358"
              data-testid="contact-manager-phone-link"
              onClick={() => trackCallConversion("contact_manager_phone")}
              className="flex items-start gap-4 group"
            >
              <div className="w-12 h-12 bg-[#D9480F] flex items-center justify-center border-2 border-white flex-shrink-0">
                <Phone className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-1">
                  Bryan Batanoff · Service Manager & Partner
                </div>
                <div className="font-display uppercase text-2xl md:text-3xl tracking-tight group-hover:text-[#F59E0B] transition-colors">
                  412-451-4358
                </div>
              </div>
            </a>

            <a
              href="mailto:raymondtjackson@bluecollarworldpestcontrolservices.com"
              data-testid={CONTACT.emailLink}
              className="flex items-start gap-4 group"
            >
              <div className="w-12 h-12 bg-[#F59E0B] flex items-center justify-center border-2 border-white flex-shrink-0">
                <Mail className="w-5 h-5 text-black" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-1">
                  Email
                </div>
                <div className="font-display uppercase text-base md:text-lg tracking-tight group-hover:text-[#F59E0B] transition-colors break-all">
                  raymondtjackson@<br className="hidden sm:inline" />bluecollarworldpestcontrolservices.com
                </div>
              </div>
            </a>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white text-black flex items-center justify-center border-2 border-white flex-shrink-0">
                <MapPin className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-1">
                  Office
                </div>
                <div className="font-display uppercase text-xl md:text-2xl tracking-tight">
                  904 Bernd Street <br />
                  Pittsburgh, PA 15210
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 border-2 border-white flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-1">
                  Hours
                </div>
                <div className="text-base">
                  Mon–Sat · 7:00 AM – 8:00 PM <br />
                  <span className="text-[#F59E0B] font-mono uppercase tracking-[0.15em] text-xs">
                    24/7 Emergency Line
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Form */}
        <div className="lg:col-span-7">
          <div className="bg-white text-zinc-900 border-2 border-black p-8 md:p-10 hard-shadow-orange">
            {success ? (
              <div
                data-testid={CONTACT.successAlert}
                className="py-10 flex flex-col items-center text-center"
              >
                <div className="w-16 h-16 bg-[#D9480F] flex items-center justify-center border-2 border-black mb-6">
                  <Phone className="w-7 h-7 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight mb-3">
                  We Got It.
                </h3>
                <p className="text-zinc-700 max-w-md">
                  Your request is in our queue. Expect a call from a real human at
                  484-764-3392 within the next 4 business hours.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-8 font-mono text-xs uppercase tracking-[0.25em] underline underline-offset-4 hover:text-[#D9480F]"
                >
                  ← Send another request
                </button>
              </div>
            ) : (
              <form data-testid={CONTACT.form} onSubmit={submit} className="space-y-5">
                <div className="border-b-2 border-black pb-4 mb-2">
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#D9480F] font-bold mb-1">
                    Request Form · BCW-001
                  </div>
                  <div className="font-display uppercase text-2xl tracking-tight">
                    Free Inspection Request
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-mono text-[11px] uppercase tracking-[0.2em] font-bold mb-2">
                      Full Name *
                    </label>
                    <input
                      data-testid={CONTACT.name}
                      type="text"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      className="w-full border-2 border-black px-4 py-3 bg-[#F4F4F5] focus:bg-white outline-none font-medium"
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[11px] uppercase tracking-[0.2em] font-bold mb-2">
                      Phone *
                    </label>
                    <input
                      data-testid={CONTACT.phone}
                      type="tel"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      className="w-full border-2 border-black px-4 py-3 bg-[#F4F4F5] focus:bg-white outline-none font-medium"
                      placeholder="412-555-0100"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[11px] uppercase tracking-[0.2em] font-bold mb-2">
                    Email *
                  </label>
                  <input
                    data-testid={CONTACT.email}
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className="w-full border-2 border-black px-4 py-3 bg-[#F4F4F5] focus:bg-white outline-none font-medium"
                    placeholder="you@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block font-mono text-[11px] uppercase tracking-[0.2em] font-bold mb-2">
                    Service Type *
                  </label>
                  <Select
                    value={form.service_type}
                    onValueChange={(v) => update("service_type", v)}
                  >
                    <SelectTrigger
                      data-testid={CONTACT.serviceType}
                      className="w-full border-2 border-black rounded-none bg-[#F4F4F5] px-4 py-6 font-medium text-zinc-900 h-auto"
                    >
                      <SelectValue placeholder="What's the problem?" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-2 border-black">
                      {serviceOptions.map((opt) => (
                        <SelectItem
                          key={opt}
                          value={opt}
                          className="rounded-none cursor-pointer focus:bg-[#F59E0B] focus:text-black"
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block font-mono text-[11px] uppercase tracking-[0.2em] font-bold mb-2">
                    What's going on? *
                  </label>
                  <textarea
                    data-testid={CONTACT.message}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    rows={5}
                    className="w-full border-2 border-black px-4 py-3 bg-[#F4F4F5] focus:bg-white outline-none font-medium resize-none"
                    placeholder="Tell us what you're seeing — when it started, where, how bad..."
                    required
                  />
                </div>

                <button
                  data-testid={CONTACT.submit}
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#D9480F] text-white px-6 py-4 font-display uppercase tracking-tight text-base hover:bg-[#E8590C] transition-colors border-2 border-black disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>Send Request →</>
                  )}
                </button>

                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 text-center pt-2">
                  We respond within 4 business hours · Bilingual support
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
